import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { BroadcastHubMessageGroupEnum } from '../enums/broadcast-hub-message-group.enum';
import { BroadcastHubEventService } from './broadcast-hub-event.service';
import { BroadcastHubMessage } from '../interfaces/broadcast-hub-message';
import localforage from 'localforage';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

interface IBroadcastHubSettings {
  host: string;
  wsServer: string;
  publicAPIKey: string;
}

interface TokenDTO {
  /** JWT Token */
  token: string;
  /** Expiry as unix seconds timestamp */
  expiry: number;
}

interface SubscriptionRequestPayload {
  data: string;
  extensions: {
    authorization: {
      host: string;
      Authorization: string;
    };
  };
}

interface WebSocketSubscriptionMessagePayload {
  data: {
    subscribe: {
      /** Channel Name */
      name: string;
      /** Data as json */
      data: string;
    };
  };
}

interface WebSocketMessage {
  id?: string;
  type: 'start' | 'start_ak' | 'ka' | 'data' | 'connection_error' | 'error';
  payload?: SubscriptionRequestPayload | WebSocketSubscriptionMessagePayload;
}

function isWebSocketSubscriptionMessagePayload(
  payload: SubscriptionRequestPayload | WebSocketSubscriptionMessagePayload,
): payload is WebSocketSubscriptionMessagePayload {
  return (payload as WebSocketSubscriptionMessagePayload).data.subscribe !== undefined;
}

@Injectable()
export class BroadcastHubService {
  private socket: WebSocketSubject<WebSocketMessage>;
  private subscriptions: {} = {};

  private readonly settingsPromise: Promise<IBroadcastHubSettings>;

  constructor(private eventService: BroadcastHubEventService, private http: HttpClient) {
    this.settingsPromise = this.http
      .get<IBroadcastHubSettings>('api/settings/broadcast-hub')
      .toPromise();
  }

  private async getWebSocketToken(): Promise<string> {
    let wsToken = await localforage.getItem<TokenDTO>('ws-token');

    if (wsToken && !this.tokenExpired(wsToken)) {
      return wsToken.token;
    }

    wsToken = await this.http.get<TokenDTO>('api/token/ws').toPromise();
    await localforage.setItem('ws-token', wsToken);
    return wsToken.token;
  }

  private async connect(): Promise<void> {
    const settings = await this.settingsPromise;

    const payload = window.btoa(JSON.stringify({}));
    const header = btoa(
      JSON.stringify({
        host: settings.host,
        'x-api-key': settings.publicAPIKey,
      }),
    );

    this.socket = webSocket<WebSocketMessage>({
      url: `wss://${settings.wsServer}?payload=${payload}&header=${header}`,
      protocol: 'graphql-ws',
    });

    this.socket.subscribe({
      next: (msg) => {
        this.messageReceived(msg);
      },
      error: (err) => console.error('Transport/WebSocket error:', err), // Called if at any point WebSocket API signals some kind of error.
      complete: () => console.log('complete'), // Called when connection is closed (for whatever reason).
    });
  }

  async subscribeToChannel(channel: string) {
    const settings = await this.settingsPromise;
    // Check to see if we have a socket open
    if (!this.socket) {
      await this.connect();
    }

    const token = await this.getWebSocketToken();

    // Check for an existing subscription
    if (this.subscriptions[channel] === undefined) {
      this.subscriptions[channel] = crypto.randomUUID();
    }

    const query = `
      subscription SubscribeToChannel($channel: String!) {
        subscribe(name: $channel) {
          name,
          data
        }
      }
    `;

    const variables = { channel };

    const data = JSON.stringify({ query, variables });

    const payload: SubscriptionRequestPayload = {
      data,
      extensions: {
        authorization: {
          Authorization: token,
          host: settings.host,
        },
      },
    };

    const subscribeRequest: WebSocketMessage = {
      id: this.subscriptions[channel],
      payload,
      type: 'start',
    };

    this.socket.next(subscribeRequest);

    console.log('Subscribing to ' + channel);
  }

  private messageReceived(msg: WebSocketMessage) {
    try {
      // msg type will be error if user is not authorised to subscribe to channel.
      if (msg.type === 'error') {
        console.error(
          'The user is likely not authorised to subscribe to this channel. Make sure you are not using localhost. Server error message:',
          msg,
        );
        return;
      }

      if (msg.type !== 'data') {
        console.log('Received appsync message of type', msg.type, '. Ignoring...');
        return;
      }

      const payload = msg.payload;

      if (!isWebSocketSubscriptionMessagePayload(payload)) {
        console.log('Unexpected payload type:', payload);
        return;
      }

      // Extract the pertinent data from the payload
      const messageObject = JSON.parse(payload?.data?.subscribe?.data) as BroadcastHubMessage<any>;

      const groupName =
        BroadcastHubMessageGroupEnum[messageObject.messageGroup] ??
        'Unknown BroadcastHubMessageGroupEnum';
      console.log('Successfully received message from', groupName);

      switch (messageObject.messageGroup) {
        case BroadcastHubMessageGroupEnum.DriverInfo:
          this.eventService.DriverInfoEvent.emit(messageObject);
          break;

        case BroadcastHubMessageGroupEnum.Telephony:
          this.eventService.TelephonyEvent.emit(messageObject);
          break;

        case BroadcastHubMessageGroupEnum.OrderImport:
          this.eventService.OrderImportEvent.emit(messageObject);
          break;

        case BroadcastHubMessageGroupEnum.DeployInspections:
          this.eventService.DeployInspectionsEvent.emit(messageObject);
          break;

        default:
          break;
      }
    } catch (err) {
      console.log('Error parsing message:', err);
    }
  }

  private tokenExpired(token: TokenDTO) {
    const clockSkewSecs = 60;
    const nowAsUnixSeconds = new Date().getTime() / 1000;
    return token.expiry + clockSkewSecs <= nowAsUnixSeconds;
  }
}
